import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eraser, Loader2 } from "lucide-react";
import { errorMessage, post } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const usePad = (open) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);
  const ctx = () => ref.current?.getContext("2d");
  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return [(e.clientX - r.left) * (ref.current.width / r.width), (e.clientY - r.top) * (ref.current.height / r.height)];
  };
  const clear = () => {
    const c = ctx();
    if (c) {
      c.clearRect(0, 0, ref.current.width, ref.current.height);
    }
    setEmpty(true);
  };
  useEffect(() => {
    if (open) {
      setTimeout(clear, 0);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const handlers = {
    onPointerDown: (e) => {
      e.preventDefault();
      ref.current.setPointerCapture(e.pointerId);
      drawing.current = true;
      const c = ctx();
      Object.assign(c, { lineWidth: 3, lineCap: "round", lineJoin: "round", strokeStyle: "#0f172a" });
      c.beginPath();
      c.moveTo(...pos(e));
    },
    onPointerMove: (e) => {
      if (!drawing.current) return;
      const c = ctx();
      c.lineTo(...pos(e));
      c.stroke();
      setEmpty(false);
    },
    onPointerUp: () => { drawing.current = false; },
    onPointerLeave: () => { drawing.current = false; },
  };
  return { ref, empty, clear, handlers };
};

export const SIG_KINDS = {
  officer: { title: "Warrant officer signature", who: "officer", ask: "Ask the officer to sign in the box below with their finger or a stylus.", tid: "signature" },
  locksmith: { title: "Locksmith signature", who: "locksmith", ask: "Sign in the box below with your finger or a stylus.", tid: "locksmith-signature" },
};

export const SignatureDialog = ({ open, onOpenChange, run, onSigned, kind = "officer" }) => {
  const k = SIG_KINDS[kind];
  const pad = usePad(open);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setName(run[`${kind}_signed_name`] || run[kind]?.full_name || "");
    }
  }, [open, run, kind]);

  const save = async () => {
    if (pad.empty || !name.trim()) {
      toast.error(`Please draw a signature and enter the ${k.who}'s name`);
      return;
    }
    setBusy(true);
    try {
      await post(`/runs/${run.id}/signature?kind=${kind}`, { name: name.trim(), image: pad.ref.current.toDataURL("image/png") });
      toast.success("Signature saved");
      onOpenChange(false);
      onSigned();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid={`${k.tid}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display">{k.title}</DialogTitle>
          <DialogDescription>{k.ask}</DialogDescription>
        </DialogHeader>
        <div className="relative rounded-lg border-2 border-dashed border-slate-300 bg-white">
          <canvas ref={pad.ref} width={900} height={320} className="block h-48 w-full touch-none sm:h-56" {...pad.handlers} data-testid={`${k.tid}-canvas`} />
          {pad.empty && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-slate-400">Sign here</span>}
          <div className="pointer-events-none absolute inset-x-6 bottom-10 border-b border-slate-300" />
        </div>
        <label className="text-sm font-medium text-slate-700">{k.who === "officer" ? "Officer's name" : "Locksmith's name"}
          <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className="mt-1" data-testid={`${k.tid}-name-input`} />
        </label>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={pad.clear} disabled={busy} data-testid={`${k.tid}-clear-button`}><Eraser className="mr-2 h-4 w-4" />Clear</Button>
          <Button onClick={save} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid={`${k.tid}-save-button`}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save signature
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
